import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { Workflow } from '@compass/client/types/workflow';
import { ChevronDown } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { CaseActionsDropdown } from '@/components/CaseActionsDropdown';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIsCaseProgramAdmin } from '@/hooks/usePermissions';
import {
  buildStatusUpdatePlans,
  filterActionsForBulkUpdate,
  getTotalUpdateCount,
  groupCasesByStatus,
  validateStatusGroups,
} from '@/utils/bulkStatusUpdate';
import { canWorkHumanAudit, categorizeActions, getActionLabel } from '@/utils/caseActions';
import { formatStatus, getStatusColors } from '@/utils/cases';

interface BulkStatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCases: GetCaseRecordResponse[];
  workflow?: Workflow | null;
  onConfirm: (statusUpdatePlans: { action: string; caseIds: string[]; count: number }[]) => void;
  isLoading?: boolean;
}

export function BulkStatusUpdateModal({
  isOpen,
  onClose,
  selectedCases,
  workflow,
  onConfirm,
  isLoading = false,
}: BulkStatusUpdateModalProps) {
  // A transportation or unifiedmedbv human audit is worked by Program Admins and above
  // only (BV-2657); anyone else gets no actions for those cases. The cases list is one
  // program, so the first case's program stands for all of them.
  const isProgramAdmin = useIsCaseProgramAdmin(
    selectedCases[0]?.organization_id,
    selectedCases[0]?.program_id,
  );

  // Group cases by status with categorized actions (filtered for bulk update)
  const statusGroups = useMemo(() => {
    const groups = groupCasesByStatus(selectedCases, workflow, state =>
      canWorkHumanAudit(state, workflow?.workflow_type, isProgramAdmin),
    );
    return groups.map(group => {
      // Filter out actions that require input (not supported in bulk update)
      const filteredActions = filterActionsForBulkUpdate(group.availableActions);
      return {
        ...group,
        availableActions: filteredActions,
        categorizedActions: categorizeActions(filteredActions),
      };
    });
  }, [selectedCases, workflow, isProgramAdmin]);

  // State for tracking selected actions for each status group
  const [selectedActions, setSelectedActions] = useState<Record<string, string>>({});

  // Create status groups with selected actions
  const statusGroupsWithActions = useMemo(() => {
    return statusGroups.map(group => ({
      ...group,
      selectedAction: selectedActions[group.status],
    }));
  }, [statusGroups, selectedActions]);

  // Check if all groups have valid selections
  const isValid = useMemo(() => {
    return statusGroupsWithActions.length > 0 && validateStatusGroups(statusGroupsWithActions);
  }, [statusGroupsWithActions]);

  // Get total count of cases that will be updated
  const totalUpdateCount = useMemo(
    () => getTotalUpdateCount(statusGroupsWithActions),
    [statusGroupsWithActions],
  );

  // Handle action selection for a status group
  const handleActionSelect = useCallback((status: string, action: string) => {
    setSelectedActions(prev => ({
      ...prev,
      [status]: action,
    }));
  }, []);

  // Handle confirm
  const handleConfirm = useCallback(() => {
    if (!isValid) return;

    const plans = buildStatusUpdatePlans(statusGroupsWithActions);
    onConfirm(plans);
  }, [isValid, statusGroupsWithActions, onConfirm]);

  // Reset selections when modal closes
  const handleClose = useCallback(() => {
    setSelectedActions({});
    onClose();
  }, [onClose]);

  // Don't render if no status groups
  if (statusGroups.length === 0) {
    return null;
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="w-[500px]! sm:w-[500px]! sm:max-w-[500px]! max-w-[500px]! overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>Bulk Status Update</SheetTitle>
          <SheetDescription>Select new status for each group</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-6">
          {statusGroups.map(group => {
            const statusColors = getStatusColors(group.status, workflow);
            const displayLabel = statusColors?.label || formatStatus(group.status, workflow);

            return (
              <div key={group.status} className="border rounded-lg p-3 space-y-3">
                {/* Header section showing status and case count */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{displayLabel}</span>
                  <span className="text-sm text-muted-foreground font-medium">
                    {group.count} case{group.count === 1 ? '' : 's'}
                  </span>
                </div>

                {/* Action section showing transition */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                      {statusColors ? (
                        <div
                          className={`${statusColors.bg} ${statusColors.text} ${statusColors.border} border inline-flex items-center px-3 py-1 rounded text-xs font-medium whitespace-nowrap`}
                        >
                          {displayLabel}
                        </div>
                      ) : (
                        <span className="bg-muted text-muted-foreground px-3 py-1 rounded-md text-sm font-medium">
                          {group.status}
                        </span>
                      )}
                      <span className="text-muted-foreground text-lg">→</span>
                    </div>

                    <div className="flex-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-auto justify-between bg-background min-w-[140px] max-w-full"
                            disabled={group.availableActions.length === 0}
                          >
                            {selectedActions[group.status]
                              ? getActionLabel(selectedActions[group.status], group.status)
                              : group.availableActions.length === 0
                                ? 'No actions available'
                                : 'Select new status'}
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        {group.availableActions.length > 0 && group.categorizedActions && (
                          <CaseActionsDropdown
                            categorizedActions={group.categorizedActions}
                            caseState={group.status}
                            onActionSelect={action => handleActionSelect(group.status, action)}
                          />
                        )}
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        {totalUpdateCount > 0 && (
          <div className="bg-muted p-3 rounded text-sm">
            <div className="font-medium">
              Ready to update {totalUpdateCount} case{totalUpdateCount === 1 ? '' : 's'}
            </div>
            <div className="text-muted-foreground mt-1 space-y-1">
              {statusGroupsWithActions
                .filter(group => group.selectedAction)
                .map((group, index) => {
                  const statusColors = getStatusColors(group.status, workflow);
                  const displayLabel = statusColors?.label || formatStatus(group.status, workflow);

                  return (
                    <div key={index}>
                      {group.count} case{group.count === 1 ? '' : 's'}: {displayLabel} →{' '}
                      {getActionLabel(group.selectedAction!, group.status)}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        <SheetFooter className="pt-6 gap-3">
          <Button variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!isValid || isLoading} className="flex-1">
            {isLoading
              ? 'Updating...'
              : `Update ${totalUpdateCount} Case${totalUpdateCount === 1 ? '' : 's'}`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
